"use client";

import React, { useState } from "react";
import { 
  Utensils, 
  Wine, 
  CheckCircle2, 
  Receipt, 
  X, 
  Send,
  FileText,
  Sparkles,
  AlertCircle
} from "lucide-react";
import { RestaurantReservation } from "@/lib/demos/restaurantDb";

interface RestaurantStageModalProps {
  reservation: RestaurantReservation;
  targetStage: "Table Reserved" | "Menu Curated" | "Billed & Paid" | "Cancelled";
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (options: {
    stage: RestaurantReservation["stage"];
    table_number?: string;
    assigned_sommelier?: string;
    dietary_notes?: string;
    tasting_menu?: string;
    customMenuNotes?: string;
    billAmount?: number;
    paymentMethod?: string;
    receiptNumber?: string;
    cancellationReason?: string;
  }) => Promise<void>;
}

export default function RestaurantStageModal({
  reservation,
  targetStage,
  isOpen,
  onClose,
  onConfirm,
}: RestaurantStageModalProps) {
  const sym = reservation.currency === "USD" ? "$" : "₹";
  const [submitting, setSubmitting] = useState(false);

  // Stage: Table Reserved
  const [tableNumber, setTableNumber] = useState(
    reservation.table_number || "Table #12 (Signature Window)"
  );
  const [assignedSommelier, setAssignedSommelier] = useState(
    reservation.assigned_sommelier || "Aarav Kapoor (Head Sommelier)"
  );

  // Stage: Menu Curated
  const [tastingMenu, setTastingMenu] = useState(reservation.tasting_menu);
  const [customMenuNotes, setCustomMenuNotes] = useState(
    reservation.dietary_notes 
      ? `Special dietary adjustments accommodated: ${reservation.dietary_notes}. Paired with Grand Cru vintage selections.`
      : "Full 7-Course progression paired with rare Burgundy & Bordeaux cellar flights."
  );

  // Stage: Billed & Paid
  const [billAmount, setBillAmount] = useState(
    reservation.estimated_spend || (reservation.currency === "USD" ? 750 : 45000)
  );
  const [paymentMethod, setPaymentMethod] = useState("Corporate Amex Centurion (POS Terminal Auth)");
  const [receiptNumber, setReceiptNumber] = useState(
    `LA-BILL-${new Date().getFullYear()}-${Math.floor(1000 + Math.random() * 9000)}`
  );

  // Stage: Cancelled
  const [cancellationReason, setCancellationReason] = useState("Guest travel delay / schedule adjustment");

  if (!isOpen) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await onConfirm({
        stage: targetStage,
        table_number: tableNumber,
        assigned_sommelier: assignedSommelier,
        tasting_menu: tastingMenu,
        customMenuNotes: customMenuNotes,
        billAmount: Number(billAmount),
        paymentMethod: paymentMethod,
        receiptNumber: receiptNumber,
        cancellationReason: cancellationReason,
      });
      onClose();
    } catch (err) {
      console.error(err);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-md animate-in fade-in duration-200">
      <div 
        className="relative w-full max-w-lg bg-stone-950 border border-amber-500/30 rounded-2xl shadow-2xl overflow-hidden flex flex-col max-h-[90vh]"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-stone-800 bg-stone-900/60">
          <div className="flex items-center gap-2.5">
            <div className="p-2 rounded-lg bg-amber-500/10 border border-amber-500/30 text-amber-400">
              {targetStage === "Table Reserved" && <Utensils className="w-5 h-5" />}
              {targetStage === "Menu Curated" && <Wine className="w-5 h-5" />}
              {targetStage === "Billed & Paid" && <Receipt className="w-5 h-5" />}
              {targetStage === "Cancelled" && <AlertCircle className="w-5 h-5 text-red-400" />}
            </div>
            <div>
              <h3 className="text-base font-bold text-white tracking-tight">
                {targetStage === "Table Reserved" && "Confirm Table & Sommelier Assignment"}
                {targetStage === "Menu Curated" && "Curate Tasting Menu & Cellar Pairings"}
                {targetStage === "Billed & Paid" && "Reconcile Dinner Bill & Issue Tax Receipt"}
                {targetStage === "Cancelled" && "Cancel Reservation & Note Reason"}
              </h3>
              <p className="text-xs text-stone-400">
                Reservation: <span className="text-white font-medium">{reservation.guest_name}</span> ({reservation.reservation_date})
              </p>
            </div>
          </div>

          <button
            onClick={onClose}
            className="p-1.5 rounded-lg text-stone-400 hover:text-white hover:bg-stone-800 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Form Body */}
        <form onSubmit={handleSubmit} className="p-6 space-y-4 overflow-y-auto">
          {/* Stage: Table Reserved */}
          {targetStage === "Table Reserved" && (
            <div className="space-y-4">
              <div>
                <label className="block text-xs font-semibold text-stone-300 mb-1">
                  Designated Table Allocation *
                </label>
                <input
                  type="text"
                  required
                  value={tableNumber}
                  onChange={(e) => setTableNumber(e.target.value)}
                  className="w-full bg-stone-900 border border-stone-800 rounded-xl px-3.5 py-2 text-sm text-white focus:outline-none focus:border-amber-500 transition-colors"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-stone-300 mb-1">
                  Assigned Lead Sommelier *
                </label>
                <select
                  value={assignedSommelier}
                  onChange={(e) => setAssignedSommelier(e.target.value)}
                  className="w-full bg-stone-900 border border-stone-800 rounded-xl px-3.5 py-2 text-sm text-white focus:outline-none focus:border-amber-500 transition-colors"
                >
                  <option value="Aarav Kapoor (Head Sommelier)">Aarav Kapoor (Head Sommelier)</option>
                  <option value="Jean-Luc Dubois (Executive Master Sommelier)">Jean-Luc Dubois (Executive Master Sommelier)</option>
                  <option value="Elena Rostova (Cellar Director)">Elena Rostova (Cellar Director)</option>
                </select>
              </div>

              <div className="p-3.5 bg-amber-500/10 border border-amber-500/30 rounded-xl text-xs text-amber-300 space-y-1">
                <div className="font-semibold flex items-center gap-1.5">
                  <Sparkles className="w-3.5 h-3.5" />
                  <span>Automated Voucher Dispatch Trigger:</span>
                </div>
                <p className="text-[11px] text-stone-300">
                  Advancing to this stage will automatically generate and attach the <strong>L'Aura Table Reservation Voucher PDF</strong> and email it to <strong>{reservation.email}</strong>.
                </p>
              </div>
            </div>
          )}

          {/* Stage: Menu Curated */}
          {targetStage === "Menu Curated" && (
            <div className="space-y-4">
              <div>
                <label className="block text-xs font-semibold text-stone-300 mb-1">
                  Culinary Tasting Progression *
                </label>
                <input
                  type="text"
                  required
                  value={tastingMenu}
                  onChange={(e) => setTastingMenu(e.target.value)}
                  className="w-full bg-stone-900 border border-stone-800 rounded-xl px-3.5 py-2 text-sm text-white focus:outline-none focus:border-amber-500 transition-colors"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-stone-300 mb-1">
                  Chef & Sommelier Pairing Specifications
                </label>
                <textarea
                  rows={3}
                  value={customMenuNotes}
                  onChange={(e) => setCustomMenuNotes(e.target.value)}
                  className="w-full bg-stone-900 border border-stone-800 rounded-xl px-3.5 py-2 text-sm text-white focus:outline-none focus:border-amber-500 transition-colors"
                />
              </div>

              <div className="p-3.5 bg-stone-900 border border-stone-800 rounded-xl text-xs text-stone-300">
                <p className="text-[11px] text-stone-400">
                  The curated menu dossier will be emailed to the guest confirming dietary accommodations and cellar flights.
                </p>
              </div>
            </div>
          )}

          {/* Stage: Billed & Paid */}
          {targetStage === "Billed & Paid" && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-semibold text-stone-300 mb-1">
                    Final Bill Amount ({sym}) *
                  </label>
                  <input
                    type="number"
                    required
                    value={billAmount}
                    onChange={(e) => setBillAmount(Number(e.target.value))}
                    className="w-full bg-stone-900 border border-stone-800 rounded-xl px-3.5 py-2 text-sm text-white focus:outline-none focus:border-amber-500 transition-colors"
                  />
                </div>

                <div>
                  <label className="block text-xs font-semibold text-stone-300 mb-1">
                    Bill Reference # *
                  </label>
                  <input
                    type="text"
                    required
                    value={receiptNumber}
                    onChange={(e) => setReceiptNumber(e.target.value)}
                    className="w-full bg-stone-900 border border-stone-800 rounded-xl px-3.5 py-2 text-sm text-white focus:outline-none focus:border-amber-500 transition-colors"
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-semibold text-stone-300 mb-1">
                  Remittance / Payment Method *
                </label>
                <select
                  value={paymentMethod}
                  onChange={(e) => setPaymentMethod(e.target.value)}
                  className="w-full bg-stone-900 border border-stone-800 rounded-xl px-3.5 py-2 text-sm text-white focus:outline-none focus:border-amber-500 transition-colors"
                >
                  <option value="Corporate Amex Centurion (POS Terminal Auth)">Corporate Amex Centurion (POS Terminal Auth)</option>
                  <option value="Visa Infinite Signature Contactless">Visa Infinite Signature Contactless</option>
                  <option value="Mastercard World Elite Direct Charge">Mastercard World Elite Direct Charge</option>
                  <option value="Corporate Bank Wire / RTGS Settlement">Corporate Bank Wire / RTGS Settlement</option>
                  <option value="Direct Room Charge (Partner Hotel Suites)">Direct Room Charge (Partner Hotel Suites)</option>
                </select>
              </div>

              <div className="p-3.5 bg-emerald-500/10 border border-emerald-500/30 rounded-xl text-xs text-emerald-300 space-y-1">
                <div className="font-semibold flex items-center gap-1.5">
                  <FileText className="w-3.5 h-3.5 text-emerald-400" />
                  <span>Automated Tax Invoice PDF Dispatch:</span>
                </div>
                <p className="text-[11px] text-stone-300">
                  Advancing to <strong>Billed & Paid</strong> will generate the official <strong>Itemized Fine Dining Tax Invoice PDF</strong> with GST & service charge breakdown and dispatch it to <strong>{reservation.email}</strong>.
                </p>
              </div>
            </div>
          )}

          {/* Stage: Cancelled */}
          {targetStage === "Cancelled" && (
            <div className="space-y-4">
              <div>
                <label className="block text-xs font-semibold text-stone-300 mb-1">
                  Reason for Cancellation *
                </label>
                <input
                  type="text"
                  required
                  value={cancellationReason}
                  onChange={(e) => setCancellationReason(e.target.value)}
                  className="w-full bg-stone-900 border border-stone-800 rounded-xl px-3.5 py-2 text-sm text-white focus:outline-none focus:border-amber-500 transition-colors"
                />
              </div>

              <div className="p-3.5 bg-red-500/10 border border-red-500/30 rounded-xl text-xs text-red-300">
                <p className="text-[11px]">
                  A courteous cancellation confirmation with an open priority rebooking invitation will be sent to the diner.
                </p>
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="pt-3 flex items-center justify-end gap-3 border-t border-stone-800">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2.5 rounded-xl bg-stone-900 hover:bg-stone-800 text-stone-300 font-medium text-xs border border-stone-800 transition-colors"
            >
              Cancel
            </button>

            <button
              type="submit"
              disabled={submitting}
              className="px-5 py-2.5 rounded-xl bg-gradient-to-r from-amber-600 to-amber-700 hover:from-amber-500 hover:to-amber-600 text-white font-bold text-xs shadow-lg shadow-amber-600/30 transition-all flex items-center gap-2 disabled:opacity-50"
            >
              {submitting ? (
                <>
                  <div className="w-3.5 h-3.5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  <span>Processing...</span>
                </>
              ) : (
                <>
                  <Send className="w-3.5 h-3.5" />
                  <span>Confirm Stage & Send Automated Mail</span>
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
